import type Phaser from 'phaser';
import type { Creature } from '@/domain/creature/types';
import type { AlchemyType } from '@/domain/genetics/types';
import {
  animationStateForMovement,
  facingFromVector,
  type LocomotionState,
  type WorldFacing,
} from '@/domain/world/actor-presentation';
import { displayHeightForStage } from '@/domain/world/world-scale';
import {
  buildCreatureRenderer,
  type CreatureRendererHandle,
  type PresentationPose,
} from './CreatureRenderer';
import {
  ActorActionStateMachine,
  type ActorActionSnapshot,
} from './actor-action-state';

export interface DirectionalActorResolution {
  facing: WorldFacing;
  locomotion: LocomotionState;
  phase: number;
  frame: number;
  changed: boolean;
}

const FACING_INDEX: Readonly<Record<WorldFacing, number>> = { down: 0, left: 1, right: 2, up: 3 };

export class DirectionalActorStateMachine {
  private facing: WorldFacing;
  private phase = 0;
  private elapsed = 0;
  private previousPresentation = '';

  constructor(initialFacing: WorldFacing = 'down') {
    this.facing = initialFacing;
  }

  resolve(resolvedX: number, resolvedY: number, deltaMs: number, reducedMotion = false): DirectionalActorResolution {
    return this.resolveState(
      facingFromVector(resolvedX, resolvedY, this.facing),
      animationStateForMovement(resolvedX, resolvedY),
      deltaMs,
      reducedMotion,
    );
  }

  force(facing: WorldFacing, locomotion: LocomotionState, deltaMs: number, reducedMotion = false): DirectionalActorResolution {
    return this.resolveState(facing, locomotion, deltaMs, reducedMotion);
  }

  private resolveState(
    facing: WorldFacing,
    locomotion: LocomotionState,
    deltaMs: number,
    reducedMotion: boolean,
  ): DirectionalActorResolution {
    this.facing = facing;
    if (locomotion === 'walk' && !reducedMotion) {
      this.elapsed += Math.max(0, deltaMs);
      this.phase = Math.floor(this.elapsed / 120) % 4;
    } else {
      this.elapsed = 0;
      this.phase = 0;
    }
    const frame = FACING_INDEX[facing] * 4 + (locomotion === 'walk' ? this.phase : 0);
    const presentation = `${facing}:${locomotion}:${this.phase}`;
    const changed = presentation !== this.previousPresentation;
    this.previousPresentation = presentation;
    return { facing, locomotion, phase: this.phase, frame, changed };
  }
}

function facingVector(facing: WorldFacing): { x: number; y: number } {
  if (facing === 'left') return { x: -1, y: 0 };
  if (facing === 'right') return { x: 1, y: 0 };
  if (facing === 'up') return { x: 0, y: -1 };
  return { x: 0, y: 1 };
}

/** The canonical house drawing is authored looking left at positive scale. */
export function canonicalFacingScale(facing: WorldFacing): -1 | 1 {
  return facing === 'right' ? -1 : 1;
}

export function actorPresentationPose(
  action: ActorActionSnapshot,
  phase: number,
  reducedMotion: boolean,
): PresentationPose {
  const direction = facingVector(action.facing);
  const pulse = reducedMotion ? 0 : phase % 2 === 0 ? -1 : 1;
  switch (action.kind) {
    case 'walk':
      return {
        x: direction.x === 0 ? pulse * 0.35 : pulse,
        y: reducedMotion ? 0 : -Math.abs(pulse),
        angle: reducedMotion ? 0 : direction.x * pulse * 2,
        scaleX: 1.02,
        scaleY: 0.98,
      };
    case 'dodge':
      return { x: direction.x * 4, y: direction.y * 2, angle: direction.x * 8, scaleX: 1.12, scaleY: 0.88 };
    case 'attack':
      return { x: direction.x * 3, y: direction.y * 2, angle: direction.x * 10, scaleX: 1.08, scaleY: 0.94 };
    case 'skill':
      return { x: 0, y: -2, angle: 0, scaleX: 0.94, scaleY: 1.1 };
    case 'hurt':
      return { x: -direction.x * 2, y: 1, angle: -direction.x * 7, scaleX: 1.06, scaleY: 0.92 };
    case 'defeated':
      return { x: 0, y: 4, angle: action.facing === 'left' ? -82 : 82, scaleX: 1.12, scaleY: 0.68 };
    case 'idle':
    default:
      return { x: 0, y: 0, angle: 0, scaleX: 1, scaleY: 1 };
  }
}

export interface DirectionalCreatureRendererOptions {
  reducedMotion?: boolean;
  initialFacing?: WorldFacing;
  scaleMultiplier?: number;
}

export interface DirectionalCreatureRendererHandle {
  container: Phaser.GameObjects.Container;
  setPosition(x: number, y: number): void;
  setMovement(resolvedX: number, resolvedY: number, deltaMs: number): void;
  setDebugPresentation(facing: WorldFacing, locomotion: LocomotionState, deltaMs: number): void;
  setDebugAction(facing: WorldFacing, action: ActorActionSnapshot['kind'], deltaMs: number): void;
  setWorldDepth(depth: number): void;
  updateCreature(creature: Creature): void;
  playAttack(facing: WorldFacing, nowMs?: number): void;
  playSkill(facing: WorldFacing, element: AlchemyType, nowMs?: number): void;
  playDodge(facing: WorldFacing, nowMs?: number): void;
  playHurt(facing: WorldFacing, nowMs?: number): void;
  playDefeated(facing: WorldFacing, nowMs?: number): void;
  destroy(): void;
}

export function buildDirectionalCreatureRenderer(
  scene: Phaser.Scene,
  initial: Creature,
  options: DirectionalCreatureRendererOptions = {},
): DirectionalCreatureRendererHandle {
  const reducedMotion = options.reducedMotion ?? false;
  const locomotionState = new DirectionalActorStateMachine(options.initialFacing);
  const actionState = new ActorActionStateMachine(options.initialFacing);
  let facing = options.initialFacing ?? 'down';
  let phase = 0;
  let moving = false;
  let creature = initial;
  let clockMs = 0;
  const renderer: CreatureRendererHandle = buildCreatureRenderer(scene, initial, {
    reducedMotion,
    showLabels: false,
    sleepMode: 'awake',
    scale: (displayHeightForStage(initial.lifecycle.stage) / 120) * (options.scaleMultiplier ?? 1),
  });

  function applyFacing(next: WorldFacing): void {
    facing = next;
    if (next === 'left' || next === 'right') renderer.setFacing(canonicalFacingScale(next));
  }

  function applyPresentation(nowMs: number): void {
    const action = actionState.update(nowMs, moving);
    applyFacing(action.facing);
    renderer.setPresentationPose(actorPresentationPose(action, phase, reducedMotion));
  }

  function play(
    command:
      | { kind: 'attack' | 'dodge' | 'hurt' | 'defeated'; facing: WorldFacing }
      | { kind: 'skill'; facing: WorldFacing; element: AlchemyType },
    nowMs = Date.now(),
  ): void {
    clockMs = Math.max(clockMs, nowMs);
    if (actionState.play(command, clockMs)) applyPresentation(clockMs);
  }

  applyPresentation(0);

  return {
    container: renderer.container,
    setPosition(x, y) {
      renderer.setStagePosition(Math.round(x), Math.round(y));
    },
    setMovement(x, y, deltaMs) {
      clockMs = Math.max(clockMs + Math.max(0, deltaMs), Date.now());
      const resolution = locomotionState.resolve(x, y, deltaMs, reducedMotion);
      facing = resolution.facing;
      phase = resolution.phase;
      moving = resolution.locomotion === 'walk';
      renderer.setWalking(moving);
      const active = actionState.update(clockMs, moving);
      if (active.kind === 'idle' || active.kind === 'walk') {
        applyFacing(facing);
      }
      applyPresentation(clockMs);
    },
    setDebugPresentation(debugFacing, locomotion, deltaMs) {
      const resolution = locomotionState.force(debugFacing, locomotion, deltaMs, reducedMotion);
      facing = resolution.facing;
      phase = resolution.phase;
      moving = locomotion === 'walk';
      renderer.setWalking(moving);
      applyFacing(facing);
      renderer.setPresentationPose(actorPresentationPose({
        kind: locomotion,
        facing,
        startedAt: clockMs,
        until: clockMs,
      }, phase, reducedMotion));
    },
    setDebugAction(debugFacing, action, deltaMs) {
      clockMs += Math.max(0, deltaMs);
      phase = Math.floor(clockMs / 120) % 4;
      facing = debugFacing;
      moving = action === 'walk';
      renderer.setWalking(moving);
      applyFacing(facing);
      renderer.setPresentationPose(actorPresentationPose({
        kind: action,
        facing,
        startedAt: clockMs,
        until: clockMs,
      }, phase, reducedMotion));
    },
    setWorldDepth(depth) { renderer.setWorldDepth(depth); },
    updateCreature(next) {
      creature = next;
      renderer.update(creature);
    },
    playAttack(nextFacing, nowMs) { play({ kind: 'attack', facing: nextFacing }, nowMs); },
    playSkill(nextFacing, element, nowMs) { play({ kind: 'skill', facing: nextFacing, element }, nowMs); },
    playDodge(nextFacing, nowMs) { play({ kind: 'dodge', facing: nextFacing }, nowMs); },
    playHurt(nextFacing, nowMs) { play({ kind: 'hurt', facing: nextFacing }, nowMs); },
    playDefeated(nextFacing, nowMs) { play({ kind: 'defeated', facing: nextFacing }, nowMs); },
    destroy() { renderer.destroy(); },
  };
}
